import { Avatar } from "@/components/drift/common";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   CompanyMark – a designed monogram for a customer. Demo-safe: no real
   logos. Companies get a mark from their stored `brand` (abbr, hue,
   variant) or a deterministic one derived from the name; private
   customers get a plain initials avatar.
   Variants: solid | tint | split | badge   (styles in index.css: .mark-*)
   ------------------------------------------------------------------ */

const HUES = [206, 214, 152, 46, 218, 332, 188, 262, 212, 198, 352, 96, 190, 238, 40, 204];
const VARIANTS = ["solid", "tint", "split", "badge"];
const SKIP = new Set(["as", "asa", "sa", "da", "og", "i", "av", "for", "the", "&", "ans", "ks", "ba", "nuf", "sameige", "sameie"]);

const hash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return Math.abs(h >>> 0); };

export function deriveBrand(name = "") {
  const words = name.replace(/[()]/g, " ").split(/\s+/).filter((w) => w && !SKIP.has(w.toLowerCase()));
  let abbr = words.map((w) => w[0]).join("").toUpperCase().slice(0, 3);
  if (abbr.length < 2 && words[0]) abbr = words[0].slice(0, 2).toUpperCase();
  const h = hash(name);
  return { abbr: abbr || "?", hue: HUES[h % HUES.length], variant: VARIANTS[(h >> 4) % VARIANTS.length] };
}

const SIZE = {
  xs: { box: "h-5 w-5 rounded-[5px]", t2: "text-[8.5px]", t3: "text-[7px]" },
  sm: { box: "h-6 w-6 rounded-md", t2: "text-[10px]", t3: "text-[8px]" },
  md: { box: "h-8 w-8 rounded-lg", t2: "text-[12px]", t3: "text-[10px]" },
  lg: { box: "h-10 w-10 rounded-[10px]", t2: "text-[14.5px]", t3: "text-[12px]" },
  xl: { box: "h-14 w-14 rounded-xl", t2: "text-[20px]", t3: "text-[16px]" },
  "2xl": { box: "h-[72px] w-[72px] rounded-2xl", t2: "text-[26px]", t3: "text-[21px]" },
};

export function CompanyMark({ name, brand, type, size = "md", className, title }) {
  if (type === "privat") return <Avatar name={name} size={size === "xs" ? "xs" : size === "2xl" || size === "xl" ? "lg" : size} tone="soft" className={className} />;
  const b = brand && brand.abbr ? brand : deriveBrand(name);
  const s = SIZE[size] || SIZE.md;
  const abbr = (b.abbr || "?").slice(0, 3);
  const tcls = abbr.length >= 3 ? s.t3 : s.t2;
  const style = { "--mk-h": b.hue };
  const base = cn("mark inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-display font-semibold leading-none tracking-[-0.03em]", s.box, className);
  const label = title ?? name;

  if (b.variant === "split") {
    return (
      <span className={cn(base, "mark-split")} style={style} title={label} aria-label={label} role="img" data-testid="company-mark" data-variant="split">
        <span className={cn("mark-split-l flex h-full w-[46%] items-center justify-center", tcls)}>{abbr[0]}</span>
        <span className={cn("mark-split-r flex h-full flex-1 items-center justify-center", tcls)}>{abbr.slice(1)}</span>
      </span>
    );
  }
  return (
    <span className={cn(base, `mark-${b.variant || "solid"}`, b.variant === "badge" && "!rounded-full", tcls)} style={style} title={label} aria-label={label} role="img" data-testid="company-mark" data-variant={b.variant || "solid"}>
      {abbr}
    </span>
  );
}
